import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { AssignablePerson, Priority, WorkItemType } from '../lib/types';
import { AssigneePicker } from '../components/Assignees';
import { WORK_ITEM_TYPE_LABELS } from '../lib/types';

interface Props {
  projectId: string;
  statusId: string;
  /** Which section the task lands in. Omitted on the kanban, where the column is the axis. */
  groupId?: string;
  people: AssignablePerson[];
  onCancel: () => void;
  onAdded: () => void;
}

/** Inline add, straight into the column it was clicked from. */
export function AddCardForm({ projectId, statusId, groupId, people, onCancel, onAdded }: Props) {
  const [title, setTitle] = useState('');
  const [type, setType] = useState<WorkItemType>('task');
  const [priority, setPriority] = useState<Priority>('medium');
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/work-items`, {
        title: title.trim(),
        type,
        priority,
        statusId,
        groupId,
        assigneeIds: assigneeIds.length > 0 ? assigneeIds : undefined,
      });
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not add that item');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="board-add-form" onSubmit={submit}>
      {error && <div className="field-error mb-2">{error}</div>}
      <textarea
        rows={2}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What needs doing?"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            void submit(e);
          }
          if (e.key === 'Escape') onCancel();
        }}
        style={{ fontSize: 13 }}
      />
      <div className="row" style={{ gap: 6, marginTop: 7 }}>
        <select value={type} onChange={(e) => setType(e.target.value as WorkItemType)} style={{ fontSize: 12, padding: '5px 7px' }}>
          {(Object.keys(WORK_ITEM_TYPE_LABELS) as WorkItemType[]).map((key) => (
            <option key={key} value={key}>
              {WORK_ITEM_TYPE_LABELS[key]}
            </option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as Priority)}
          style={{ fontSize: 12, padding: '5px 7px' }}
        >
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
          <option value="none">No priority</option>
        </select>
      </div>
      <div style={{ marginTop: 6 }}>
        <AssigneePicker
          value={people
            .filter((person) => assigneeIds.includes(person.id))
            .map((person) => ({ id: person.id, name: person.name }))}
          people={people}
          onChange={(next) => setAssigneeIds(next.map((person) => person.id))}
        />
      </div>
      <div className="row" style={{ gap: 6, marginTop: 8 }}>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !title.trim()}>
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button type="button" className="btn btn-quiet btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
